import { useRouter } from 'next/router';
import { FIND_ONBOARDING_PAGE, getFindPageUrl } from 'constants/pages';
import { SportsEnum } from 'types/generated/client';
import { pluralize } from 'utils/shared/pluralize';
import { useFindPlayersOnboarding } from 'hooks/useFindPlayersOnboarding';
import { useRequireAuth } from 'hooks/useRequireAuth';
import PickleRacket from 'svg/PickleRacket';
import Racket from 'svg/Racket';
import { FindTab } from 'screens/Find/types';
import { Button } from 'components/Button';
import FindPlayersLockup from 'components/FindPlayersLockup';
import Link from 'components/Link';
import classNames from 'styles/utils/classNames';
import { useNearbyPlayersCount } from './useNearbyPlayersCount';

export interface FindPlayersRequestSummary {
  /** The listing id — an event id (GAME) or availability id (AVAILABILITY). */
  id: string;
  /** Which kind of listing the requests belong to. */
  type: 'GAME' | 'AVAILABILITY';
  sport: SportsEnum;
  /** e.g. "Doubles • NTRP 3.5 - 4.0 • Advanced" */
  title: string;
  requestCount: number;
}

interface Props {
  /** When any listing has pending requests, the CTA banner becomes a summary card. */
  requests?: FindPlayersRequestSummary[];
  /** Opens the listing's host detail modal (with its incoming requests). */
  onOpenRequests?: (request: FindPlayersRequestSummary) => void;
  /** Drop when the banner is embedded inside another section's card. */
  withContainer?: boolean;
  ctaLabel?: string;
  ctaIcon?: React.ReactNode;
}

const LISTING_TYPE_LABELS: Record<FindPlayersRequestSummary['type'], string> = {
  GAME: 'Game',
  AVAILABILITY: 'Availability',
};

const CARD_CLASSES =
  'rounded-3xl bg-brand-fire-600 bg-[radial-gradient(140%_190%_at_80%_18%,#FF6610_0%,#F54519_40%,rgba(223,67,32,0)_88%)] shadow-[0px_4px_20px_0px_rgba(255,92,51,0.5)]';

export default function FindPlayersSection({
  requests = [],
  onOpenRequests,
  withContainer = true,
  ctaLabel = 'Get started',
  ctaIcon = (
    <img
      src="/images/player-home-v2/discover/search-colored.png"
      alt=""
      className="h-4 w-4 shrink-0 object-contain"
    />
  ),
}: Props) {
  const router = useRouter();
  const { hasCompletedOnboarding } = useFindPlayersOnboarding();
  const { count: nearbyPlayersCount, city } = useNearbyPlayersCount();
  // The questionnaire saves onto the viewer's profile, so a logged-out tap logs in
  // first and lands there afterwards rather than on a form with nowhere to save.
  const { requireAuth, authModals } = useRequireAuth();
  const destination = hasCompletedOnboarding
    ? getFindPageUrl(FindTab.Players)
    : FIND_ONBOARDING_PAGE;

  const Wrapper = withContainer ? 'section' : 'div';

  return (
    <Wrapper className={withContainer ? 'container relative mx-auto' : 'relative'}>
      {requests.length === 0 ? (
        <Link
          href={destination}
          onClick={(event) => {
            event.preventDefault();
            requireAuth(() => router.push(destination));
          }}
          className={classNames(
            CARD_CLASSES,
            'flex items-center justify-center px-ds-xl py-ds-lg max-lg:flex-col max-lg:gap-ds-md lg:justify-between lg:gap-ds-lg',
          )}
        >
          <FindPlayersLockup
            toneClassName="text-color-text-lightmode-invert"
            className="lg:flex-row lg:gap-ds-md"
          />
          <div className="flex shrink-0 items-center gap-ds-lg max-lg:w-full max-lg:flex-col">
            {nearbyPlayersCount > 0 && (
              <span className="typography-product-button-label-medium rounded-full bg-white/10 px-ds-lg py-ds-sm text-color-text-lightmode-invert max-lg:w-full max-lg:text-center">
                {pluralize({ count: nearbyPlayersCount, singular: 'player' })} looking for a partner
                in {city ?? 'your area'}
              </span>
            )}
            {/* Presentational — the whole banner is the link, so the click falls through. */}
            <Button
              variant="primary-light"
              size="md"
              isInline
              tabIndex={-1}
              className="pointer-events-none whitespace-nowrap max-lg:hidden"
              iconLeft={ctaIcon}
            >
              {ctaLabel}
            </Button>
          </div>
        </Link>
      ) : (
        <div
          className={classNames(
            CARD_CLASSES,
            'flex flex-col items-center gap-ds-md p-ds-lg lg:flex-row lg:items-start lg:gap-ds-xl lg:p-ds-xl',
          )}
        >
          <h2 className="typography-product-heading-mobile lg:typography-product-heading-compact-desktop text-color-text-lightmode-invert lg:shrink-0">
            Requests: {requests.reduce((total, request) => total + request.requestCount, 0)}
          </h2>
          <div className="h-px w-full bg-color-border-input-lightmode lg:h-auto lg:w-px lg:self-stretch lg:bg-color-border-input-lightmode/40" />

          <div className="contents lg:flex lg:min-w-0 lg:flex-1 lg:flex-col lg:gap-ds-md">
            {requests.map((request) => {
              const SportIcon = request.sport === SportsEnum.Pickleball ? PickleRacket : Racket;

              return (
                <div key={request.id} className="flex w-full items-center gap-ds-sm">
                  <div className="flex min-w-0 flex-1 flex-col items-start gap-ds-xs">
                    <span className="flex h-5 items-center gap-ds-xs rounded-2xl bg-white/10 px-ds-sm shadow-[0px_4px_16px_0px_rgba(0,0,0,0.08)]">
                      <SportIcon className="h-3 w-3 shrink-0 text-color-text-lightmode-invert" />
                      <span className="typography-product-button-label-xs text-color-text-lightmode-invert">
                        {LISTING_TYPE_LABELS[request.type]}
                      </span>
                    </span>
                    <span className="typography-product-subheading-mobile text-color-text-lightmode-invert">
                      {request.title}
                    </span>
                  </div>

                  <Button
                    variant="primary-light"
                    size="sm"
                    isInline
                    onClick={() => onOpenRequests?.(request)}
                    className="shrink-0 whitespace-nowrap"
                  >
                    {pluralize({ count: request.requestCount, singular: 'Request' })}
                  </Button>
                </div>
              );
            })}
          </div>
        </div>
      )}
      {authModals}
    </Wrapper>
  );
}
